import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { clientContactService, clientService } from "@/services/clientService";
import type {
  BankAccount,
  ClientAddress,
  ClientContactCreatePayload,
  ClientContactUpdatePayload,
  ClientCreatePayload,
  ClientUpdatePayload,
} from "@/types/client";

const keys = {
  list: (wsId: number, orgId?: number) => ["clients", wsId, orgId ?? "all"] as const,
  one: (wsId: number, id: number) => ["clients", wsId, "one", id] as const,
  contacts: (wsId: number, clientId: number) => ["client-contacts", wsId, clientId] as const,
  cert: (wsId: number, clientId: number) => ["client-certificate", wsId, clientId] as const,
};

export function useClientCertificate(workspaceId: number | null, clientId: number | null) {
  return useQuery({
    queryKey: keys.cert(workspaceId ?? 0, clientId ?? 0),
    queryFn: () => clientService.getCertificate(workspaceId as number, clientId as number),
    enabled: workspaceId != null && clientId != null,
  });
}

export function useUploadClientCertificate(workspaceId: number, clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, password }: { file: File; password: string }) =>
      clientService.uploadCertificate(workspaceId, clientId, file, password),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.cert(workspaceId, clientId) }),
  });
}

export function useRevokeClientCertificate(workspaceId: number, clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => clientService.revokeCertificate(workspaceId, clientId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.cert(workspaceId, clientId) }),
  });
}

export function useClients(workspaceId: number | null, organizationId?: number) {
  return useQuery({
    queryKey: keys.list(workspaceId ?? 0, organizationId),
    queryFn: () => clientService.list(workspaceId as number, organizationId),
    enabled: workspaceId != null,
  });
}

export function useClient(workspaceId: number | null, clientId: number | null) {
  return useQuery({
    queryKey: keys.one(workspaceId ?? 0, clientId ?? 0),
    queryFn: () => clientService.get(workspaceId as number, clientId as number),
    enabled: workspaceId != null && clientId != null,
  });
}

export function useCreateClient(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ClientCreatePayload) => clientService.create(workspaceId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clients", workspaceId] }),
  });
}

export function useUpdateClient(workspaceId: number, clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ClientUpdatePayload) =>
      clientService.update(workspaceId, clientId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["clients", workspaceId] });
      void qc.invalidateQueries({ queryKey: keys.one(workspaceId, clientId) });
    },
  });
}

export function useDeleteClient(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clientId: number) => clientService.remove(workspaceId, clientId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clients", workspaceId] }),
  });
}

export function useSetClientAddress(workspaceId: number, clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ClientAddress) =>
      clientService.setAddress(workspaceId, clientId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.one(workspaceId, clientId) }),
  });
}

export function useSetClientBank(workspaceId: number, clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BankAccount) => clientService.setBank(workspaceId, clientId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.one(workspaceId, clientId) }),
  });
}

export function useResponsibleLookup(workspaceId: number) {
  return useMutation({
    mutationFn: (cpf: string) => clientService.lookupResponsible(workspaceId, cpf),
  });
}

export function useClientContacts(workspaceId: number | null, clientId: number | null) {
  return useQuery({
    queryKey: keys.contacts(workspaceId ?? 0, clientId ?? 0),
    queryFn: () => clientContactService.list(workspaceId as number, clientId as number),
    enabled: workspaceId != null && clientId != null,
  });
}

export function useCreateContact(workspaceId: number, clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ClientContactCreatePayload) =>
      clientContactService.create(workspaceId, clientId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.contacts(workspaceId, clientId) }),
  });
}

export function useUpdateContact(workspaceId: number, clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { contactId: number; payload: ClientContactUpdatePayload }) =>
      clientContactService.update(workspaceId, clientId, args.contactId, args.payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.contacts(workspaceId, clientId) }),
  });
}

export function useDeleteContact(workspaceId: number, clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contactId: number) =>
      clientContactService.remove(workspaceId, clientId, contactId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.contacts(workspaceId, clientId) }),
  });
}
